import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ThumbsUp, ThumbsDown, ExternalLink, Lock, Sparkles } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { MarkdownReader } from "@/components/reports/v32/MarkdownReader";
import { ApiError } from "@/api/client";
import {
  knowledgeBaseApi,
  type KbAnswerResult,
  type KbCitation,
  type KbMyAccess,
} from "@/api/knowledgeBase";

// Knowledge Base v1.0 PR 4 — Ask surface. Mirrors the AnswerAssistantModal
// low-chrome voice: large textarea, single primary action, honest "not found"
// state, inline 429 quota message with the "limit resets at midnight UTC"
// copy. Source open/download links are enabled only when the source's
// category is in the caller's `accessibleCategoryCodes` (or `isManager`);
// otherwise the row renders a subtle "Restricted" indicator.

interface KbAskTabProps {
  myAccess: KbMyAccess | undefined;
  categories: { code: string; name: string }[];
}

const QUOTA_MESSAGE =
  "You've reached today's limit of questions. The limit resets at midnight UTC.";

export function KbAskTab({ myAccess, categories }: KbAskTabProps) {
  const [question, setQuestion] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [answer, setAnswer] = useState<KbAnswerResult | null>(null);
  const [feedback, setFeedback] = useState<"Helpful" | "NotHelpful" | null>(null);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const askMutation = useMutation({
    mutationFn: () =>
      knowledgeBaseApi.ask(question.trim(), categoryFilter || null),
    onSuccess: (result) => {
      setAnswer(result);
      setFeedback(null);
      setErrorMessage(null);
      setQuotaExhausted(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        setQuotaExhausted(true);
        setErrorMessage(QUOTA_MESSAGE);
      } else if (err instanceof ApiError) {
        setErrorMessage(err.errors?.[0] ?? err.message);
      } else {
        setErrorMessage(err instanceof Error ? err.message : "Request failed.");
      }
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: ({ queryId, value }: { queryId: string; value: "Helpful" | "NotHelpful" }) =>
      knowledgeBaseApi.submitFeedback(queryId, value),
    onSuccess: (_d, vars) => setFeedback(vars.value),
  });

  const trimmed = question.trim();
  const canSubmit = trimmed.length > 0 && !askMutation.isPending && !quotaExhausted;

  return (
    <div className="space-y-space-4">
      <div>
        <h2 className="font-display font-bold text-h3 text-onyx normal-case">
          Ask a question
        </h2>
        <p className="text-body text-gray-mid mt-space-1 max-w-reading">
          Ask a question about Phoenix Capital's documents, contracts, and procedures.
          Answers cite the source documents — the assistant will tell you when it
          can't find an answer.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) askMutation.mutate();
        }}
        className="space-y-space-3"
      >
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. What is our policy on counterparty diligence renewals?"
          aria-label="Your question"
          rows={4}
          disabled={askMutation.isPending}
        />
        <div className="flex flex-col md:flex-row md:items-center gap-space-3">
          <div className="flex-1 min-w-0">
            <label
              htmlFor="kb-category-filter"
              className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1"
            >
              Category filter (optional)
            </label>
            <Select
              id="kb-category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              disabled={askMutation.isPending}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="md:self-end">
            <Button type="submit" disabled={!canSubmit} loading={askMutation.isPending}>
              <Sparkles size={16} aria-hidden /> Generate answer
            </Button>
          </div>
        </div>
      </form>

      {askMutation.isPending && (
        <div className="font-body text-body text-gray-mid italic" role="status">
          Searching the knowledge base…
        </div>
      )}

      {errorMessage && !askMutation.isPending && (
        <div
          role="alert"
          className="text-caption text-status-error border border-status-error/30 bg-status-error/5 rounded-md p-space-3"
        >
          {errorMessage}
        </div>
      )}

      {answer && !askMutation.isPending && (
        <AnswerPanel
          result={answer}
          myAccess={myAccess}
          feedback={feedback}
          onFeedback={(value) => {
            if (feedbackMutation.isPending) return;
            feedbackMutation.mutate({ queryId: answer.queryId, value });
          }}
        />
      )}
    </div>
  );
}

interface AnswerPanelProps {
  result: KbAnswerResult;
  myAccess: KbMyAccess | undefined;
  feedback: "Helpful" | "NotHelpful" | null;
  onFeedback: (value: "Helpful" | "NotHelpful") => void;
}

function AnswerPanel({ result, myAccess, feedback, onFeedback }: AnswerPanelProps) {
  if (!result.wasAnswered) {
    return (
      <section
        aria-label="Answer"
        className="border-t-[2px] border-t-gold bg-white-pure rounded-lg shadow-sm p-space-4"
      >
        <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">
          I couldn't find this in the knowledge base
        </h3>
        <p className="font-body text-body text-gray-dark">
          The knowledge base doesn't have a confident match for your question. Try
          rephrasing it, narrowing the category filter, or asking a teammate to
          upload the source document if it should be here.
        </p>
        <FeedbackRow feedback={feedback} onFeedback={onFeedback} />
      </section>
    );
  }

  return (
    <section
      aria-label="Answer"
      className="border-t-[2px] border-t-gold bg-white-pure rounded-lg shadow-sm p-space-4 space-y-space-3"
    >
      <div>
        <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Answer</h3>
        {/* The answer is Claude-generated markdown (headings, **bold**, lists,
            tables). Render it through the staff MarkdownReader — the same
            renderer used for AI assessment markdown on the deal/collection
            staging panels — instead of as raw pre-wrapped text. */}
        <MarkdownReader markdown={result.answer} />
      </div>

      {result.sources.length > 0 && (
        <div>
          <h4 className="font-display font-semibold text-small uppercase tracking-label text-gray-mid mb-space-2">
            Sources
          </h4>
          <ul className="divide-y divide-border-light border border-border-light rounded-md">
            {result.sources.map((src) => (
              <li key={src.documentId}>
                <SourceRow citation={src} myAccess={myAccess} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <FeedbackRow feedback={feedback} onFeedback={onFeedback} />
    </section>
  );
}

interface SourceRowProps {
  citation: KbCitation;
  myAccess: KbMyAccess | undefined;
}

function SourceRow({ citation, myAccess }: SourceRowProps) {
  const canOpen =
    myAccess?.isManager ||
    (myAccess?.accessibleCategoryCodes ?? []).some(
      (c) => c.toLowerCase() === citation.category.toLowerCase(),
    );
  const [opening, setOpening] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleOpen = async () => {
    if (opening) return;
    setOpening(true);
    setLinkError(null);
    try {
      const link = await knowledgeBaseApi.getDownloadLink(citation.documentId);
      window.open(link.sasUrl, "_blank", "noopener");
    } catch (err) {
      setLinkError(
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Could not open document.",
      );
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-space-3 px-space-3 py-space-2">
      <div className="flex-1 min-w-0">
        <div className="font-body text-small text-onyx font-semibold truncate">
          {citation.title}
        </div>
        <div className="text-caption text-gray-mid">{citation.category}</div>
        {linkError && (
          <div className="text-caption text-status-error mt-space-1" role="alert">
            {linkError}
          </div>
        )}
      </div>
      {canOpen ? (
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          onClick={handleOpen}
          loading={opening}
          aria-label={`Open ${citation.title}`}
        >
          <ExternalLink size={14} aria-hidden /> Open
        </Button>
      ) : (
        <span
          className="inline-flex items-center gap-space-1 text-caption text-gray-mid italic"
          title="Visible to authorized positions only."
        >
          <Lock size={12} aria-hidden /> Restricted — visible to authorized positions
        </span>
      )}
    </div>
  );
}

interface FeedbackRowProps {
  feedback: "Helpful" | "NotHelpful" | null;
  onFeedback: (value: "Helpful" | "NotHelpful") => void;
}

function FeedbackRow({ feedback, onFeedback }: FeedbackRowProps) {
  return (
    <div className="flex items-center gap-space-2 pt-space-2 border-t border-border-light">
      <span className="text-caption text-gray-mid">Was this helpful?</span>
      <button
        type="button"
        onClick={() => onFeedback("Helpful")}
        aria-label="Mark answer helpful"
        aria-pressed={feedback === "Helpful"}
        className={
          feedback === "Helpful"
            ? "p-space-1 rounded-md text-gold bg-gold/10"
            : "p-space-1 rounded-md text-gray-mid hover:text-gold hover:bg-white-warm"
        }
      >
        <ThumbsUp size={14} aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => onFeedback("NotHelpful")}
        aria-label="Mark answer not helpful"
        aria-pressed={feedback === "NotHelpful"}
        className={
          feedback === "NotHelpful"
            ? "p-space-1 rounded-md text-status-error bg-status-error/10"
            : "p-space-1 rounded-md text-gray-mid hover:text-status-error hover:bg-white-warm"
        }
      >
        <ThumbsDown size={14} aria-hidden />
      </button>
    </div>
  );
}
